import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { FdsIconType } from '@tcc/fds';
import { useImpersonationMode } from './useImpersonationMode';
import { useSubMenuDescriptors } from './useSubMenuDescriptors';
import { byExcludedImpersonationModes, byRequiredFeatureFlags, byExcludedByFeaureFlag } from './useNavigationLinks/filters';
import { descriptorToNavigationLink } from './useNavigationLinks/mappers';
import { User } from '../types/user';

type SubMenuItemProps = {
  label: string;
  leadingIcon?: FdsIconType;
  dataTestId?: string;
  onClick: () => void;
};

interface UseUserSubMenuParams {
  featureFlags?: Record<string, boolean>;
  user: User;
}

export const useUserSubMenu = ({
  featureFlags,
  user,
}: UseUserSubMenuParams): SubMenuItemProps[] => {
  const location = useLocation();
  const { impersonationModes, isLoading } = useImpersonationMode();

  const descriptors = useSubMenuDescriptors({ 
    countryCode: user?.details.countryCode,
    isCommerceCloudEnabled: featureFlags?.['farmer-navigation.isCommerceCloudEnabled'],
  });

  const filterDescriptors = useMemo(() => {
    if (isLoading) return [];

    return descriptors
      .filter(descriptor => byExcludedImpersonationModes(impersonationModes, descriptor))
      .filter(descriptor => byRequiredFeatureFlags(featureFlags, descriptor))
      .filter(descriptor => byExcludedByFeaureFlag(featureFlags, descriptor));
  }, [isLoading, descriptors, impersonationModes, featureFlags]);

  return useMemo(() => 
    filterDescriptors.map(descriptor => {
      const navLink = descriptorToNavigationLink(descriptor, location);

      return {
        label: navLink.label,
        leadingIcon: descriptor.leadingIcon,
        dataTestId: navLink.testId,
        onClick: () => window.location.assign(navLink.href),
      };
    }), [filterDescriptors, location]);
};
